import cloneDeep from 'lodash/cloneDeep';
import { transaction } from '@src/slide/domain/slide-app';
import { getTextLength } from '@src/slide/domain/ui-model/element-modeles/property/text/utils/utils';
import { splitTextRuns } from '@editor/src/page/index/components/editor/data/textrun';
import { UIModelType } from '@src/slide/domain/ui-model/element-modeles/constant';
import { TEXT_EFFECT_KEY, TEXT_STROKE_KEY, TEXT_FILL_LIST, TEXT_BASELINE_OFFSET } from '@src/slide/domain/ui-model/element-modeles/property/text/constants';
import { ISelectionService } from '@src/slide/application/service/selection/selection-service.interface';
import SelectedEleManager from '@src/slide/application/service/selection/selected-ele-manager';
import { IGroupModel } from '@src/slide/domain/ui-model/element-modeles/Interface/group-model.interface';
import { ITableModel } from '@src/slide/domain/ui-model/element-modeles/Interface/table-model.interface';
import { IShapeModel } from '@src/slide/domain/ui-model/element-modeles/Interface/shape-model.interface';
import {
    getPlaceHolderType, isTextSomeAsDefault, judgeTextIsEmpty,
} from '@editor/src/page/index/components/canvas/element-types/shape-element/utils';
import { getStartEndPoint } from '@src/slide/domain/ui-model/element-modeles/property/text/utils/selection';

// 更新类型（table和shape的数据更新api不一样，所以区分）
enum UPDATE_OPERATE_TYPE {
    TABLE = 'table',
    SHAPE = 'shape'
}

// 继承中有效的textRun属性
const EXTEND_TEXT_RUN_STYLE = [
    'del',
    'backgroundColor',
    'color',
    'tdColor',
    'fontSize',
    'fontWeight',
    'fontStyle',
    'textDecoration',
    'gradFill',
    'fonts',
    'fontFamily',
    'letterSpacing',
    TEXT_EFFECT_KEY,
    ...TEXT_FILL_LIST,
    TEXT_STROKE_KEY,
    TEXT_BASELINE_OFFSET,
];

/**
 * 格式清除工具类
 * 入口为executeClear
 */
class ClearFormat {
    // 获取当前选中元素的工具类
    private selectedElementsManager: SelectedEleManager | undefined;
    private selectionService: ISelectionService | undefined;

    public setSelectionServiceDelegate(selectionService: ISelectionService): void {
        if (this.selectionService) return;
        this.selectionService = selectionService;
        this.selectedElementsManager = selectionService.selectedElements;
    }


    /**
     * 格式清除入口（点击清除按钮后调用此方法）
     * （1）获取slide中被选中的元素
     * （2）为保证undo/redo操作，数据的更新需要批处理。所以先将所有可以被格式化的元素的数据进行格式化
     *     也就是删除shape -> paragraphList[]  -> textRuns[] -> styles下可被删除的属性
     * （3）通过transaction实现批量更新（注意table调用table的方法，shape调用shape的方法）
     */
    public executeClear() {
        // 获取当前选中项，没有则直接退出
        const selectedArray = this.selectedElementsManager?.selectedArray?.filter?.(e => !!e);
        // 按元素类型区分，生成等待批量更新的处理后的数据
        let resArray: Array<any> = [];
        selectedArray?.forEach((selectedItem) => {
            switch (selectedItem?.type) {
                // 组合元素（因为组合元素会包含几种元素，需要拆分后分别处理，所以单拎出来）
                case UIModelType.GROUP:
                    resArray = [...resArray, ...this.dealGroup(selectedItem as IGroupModel)];
                    break;
                // 表格元素（因为表格的数据分析以及更新特殊，所以单拎出来）
                case UIModelType.TABLE:
                    resArray.push(this.dealTable(selectedItem as ITableModel));
                    break;
                // shape元素（文本、链接、图形中的一种）
                case UIModelType.SHAPE:
                    resArray.push(this.dealShape(selectedItem as IShapeModel));
                    break;
                case UIModelType.CHART: {
                    const res = this.dealChart(selectedItem as IShapeModel);
                    if (res) {
                        resArray.push(res);
                    }
                    break;
                }
            }
        });
        // 将处理后的数据做更新操作（因为涉及到撤销，所以需要批量操作）
        transaction(() => {
            resArray.forEach((res) => {
                if (res.type === UPDATE_OPERATE_TYPE.TABLE) {
                    // 表格元素的数据更新必须使用表格自己的方法
                    const { tableItem, props } = res;
                    tableItem.updateTableAllParagraphList(props);
                } else {
                    // 其他类型统一走shape元素更新的方法
                    const { shapeItem, paragraphList } = res;
                    this.updateShapeProps(shapeItem, paragraphList);
                }
            });
        });
    }
    /**
     * 更新shape元素属性
     * @param shapeItem 元素
     * @param paragraphList 最新的段落文本样式
     * @private
     */
    private updateShapeProps(shapeItem, paragraphList) {
        if (shapeItem.isPlaceholder && shapeItem.isFromLayout) {
            const type = getPlaceHolderType(shapeItem);
            // 占位符文字是否自定义
            const isTextCustom = !judgeTextIsEmpty(paragraphList)
                && !isTextSomeAsDefault(paragraphList, type);
            shapeItem.updateMasterAndLayoutPlaceHolder(paragraphList, null, isTextCustom);
        } else {
            shapeItem.updateParagraphList(paragraphList);
        }
    }

    /**
     * 组合元素的数据加工
     * 拆分成表格元素、shape元素分别处理
     * @param groupItem [(表格元素 || shape元素)...]
     * @private
     */
    private dealGroup(groupItem: IGroupModel) {
        let resArr: Array<any> = [];
        (groupItem?.children || []).forEach((item) => {
            switch (item?.type) {
                // 组合元素
                case UIModelType.GROUP:
                    resArr = [...resArr, ...this.dealGroup(item as IGroupModel)];
                    break;
                // 表格元素(表格暂时不支持组合，此处逻辑为了扩展保留)
                case UIModelType.TABLE:
                    resArr.push(this.dealTable(item as ITableModel));
                    break;
                // shape元素（文本、链接、图形中的一种）
                case UIModelType.SHAPE:
                    resArr.push(this.dealShape(item as IShapeModel));
                    break;
                case UIModelType.CHART: {
                    const result = this.dealChart(item as IShapeModel);
                    if (result) {
                        resArr.push(result);
                    }
                    break;
                }
            }
        });
        return resArr;
    }

    /**
     * 表格元素的数据加工
     * @param tableItem 表格类元素
     * @private
     */
    private dealTable(tableItem: ITableModel) {
        // 遍历表格的行，然后每行再遍历 行列交叉即为单元格（每个单元格都是一个shape元素）
        const items = (tableItem?.props?.items || [])
            .map((rowItem: any, rowIndex) => (rowItem.map((shapeItem, columnIndex) => {
                const isMerged = shapeItem.hMerge || shapeItem.vMerge;
                const isNotSelected = !tableItem.selectedRange
                    || !tableItem.cellSelectedState(rowIndex, columnIndex, tableItem.selectedRange);
                if (isMerged || isNotSelected) {
                    // 如果是被合并的单元格或者是未被选中的单元格则不做处理
                    return undefined;
                }
                // 其余场景则都需要处理，单元格内嵌入的一个个的shape（注意：table内的shape需要统一走table的方法进行更新，不能直接用shape的方法）
                const { paragraphList } = this.dealShape(shapeItem);
                return {
                    paragraphList,
                    textBodyStyle: shapeItem.textBodyStyle,
                };
            })));
        return {
            type: UPDATE_OPERATE_TYPE.TABLE,
            tableItem,
            props: { items },
        };
    }

    /**
     * 通用元素（文本、链接、图形）的数据加工
     * @param shapeItem shape类元素
     * @private
     */
    private dealShape(shapeItem: IShapeModel) {
        let paragraphList;
        const selection = shapeItem?.editorInstance?.getSelection();
        // 是否为编辑态的shape
        const isEditingShape = shapeItem?.editorInstance?.isEditing && !!selection;
        if (isEditingShape) {
            // 编辑态的特殊处理，因为可能存在需要拆分的情况（比如文本局部选中）
            paragraphList = this.dealEditingShape(shapeItem);
        } else {
            // 其他场景统一直接清除就可以了
            paragraphList = this.dealNormalShape(shapeItem);
        }
        return {
            type: UPDATE_OPERATE_TYPE.SHAPE,
            shapeItem,
            paragraphList,
        };
    }

    private dealChart(chart: IShapeModel) {
        const selection = chart?.editorInstance?.getSelection();
        const isEditingShape = chart?.editorInstance?.isEditing && !!selection;

        // 如果图表标题不是编辑态，先不支持格式清除
        if (!isEditingShape) {
            return;
        }

        const paragraphList = this.dealEditingShape(chart);

        return {
            type: UPDATE_OPERATE_TYPE.SHAPE,
            shapeItem: chart,
            paragraphList,
        };
    }

    /**
     * 处理编辑态的shape元素
     * 如果是局部选中的需要做textRun拆分，然后再对选中的textRun做清除
     * @param shapeItem
     */
    // eslint-disable-next-line complexity
    private dealEditingShape(shapeItem: IShapeModel) {
        const selection = shapeItem?.editorInstance?.getSelection();

        const srcData = shapeItem?.editorInstance?.getData?.();
        const paragraphList = cloneDeep(srcData?.paragraphList || []);
        // 获取选中区所在段落头尾index、在所属段落的偏移量头尾index
        const {
            sIndex = 0,
            eIndex = paragraphList.length - 1,
            sOffset = 0,
            eOffset = 0,
        } = getStartEndPoint(selection?.anchor, selection?.focus);
        // 遍历所有段落，将选中区的textRuns拆分并清除格式
        for (let i = sIndex; i <= eIndex; i++) {
            // 获取选中区在当前段落的头尾偏移量
            const curSOffset = (selection && sIndex === i) ? sOffset : 0;
            const curEOffset = (selection && eIndex === i) ? eOffset : getTextLength(paragraphList[i]);
            const oldTextRuns = paragraphList[i].textRuns;
            // 空文本或者只是点击但没有选择文字这两种场景，不做处理，直接continue检查下个段落
            if (getTextLength({ textRuns: oldTextRuns }) === 0 || curSOffset === curEOffset) {
                continue;
            }
            // 有具体选中文字的场景，textRuns做拆分并将被选中覆盖的textRun做格式清除
            const {
                textRuns: splitResTextRuns,
                startIndex: splitResStartIndex,
                endIndex: splitResEndIndex,
            } = splitTextRuns(oldTextRuns, curSOffset, curEOffset);
            if (splitResStartIndex !== null && splitResEndIndex !== null) {
                for (let j = splitResStartIndex; j <= splitResEndIndex; j++) {
                    splitResTextRuns[j] = this.resetTextRunStyles(splitResTextRuns[j]);
                }
            }
            paragraphList[i].textRuns = splitResTextRuns;
        }
        return paragraphList;
    }

    /**
     * 处理普通选中态的shape元素
     * 直接清除所有textRun下styles部分属性即可
     * @param shapeItem
     * @private
     */
    private dealNormalShape(shapeItem: IShapeModel) {
        const paragraphList = cloneDeep(shapeItem?.paragraphList || []);
        paragraphList.forEach((paragraph, index) => {
            // 空文本框清空历史样式
            paragraphList[index].endParaRPr = this.resetEndParaRPr(paragraph.endParaRPr);
            // 非空文本框清空当前文字样式
            paragraphList[index].textRuns = paragraph.textRuns
                ?.map(textRun => this.resetTextRunStyles(textRun));
        });
        return paragraphList;
    }

    /**
     * 重置空文本框的历史文本样式
     * @param endParaRPr
     * @private
     */
    private resetEndParaRPr(endParaRPr) {
        const cloneEndParaRPr = cloneDeep(endParaRPr);
        if (cloneEndParaRPr?.useEndParaStyle) {
            EXTEND_TEXT_RUN_STYLE.forEach((prop) => {
                if (prop in (cloneEndParaRPr || {})) {
                    cloneEndParaRPr[prop] = null;
                    cloneEndParaRPr[`raw-${prop}`] = true;
                }
            });
        }
        return cloneEndParaRPr;
    }

    /**
     * 重置文本样式
     * @param textRun  shape -> paragraphList[]  -> textRuns[] -> styles （元素 ->段落 ->文本片 ->样式）
     * @private 重置样式后的文本片(指定的style属性才删除，因为链接元素styles里有不能删除的属性)
     */
    private resetTextRunStyles(textRun) {
        const cloneTextRun = cloneDeep(textRun);
        EXTEND_TEXT_RUN_STYLE.forEach((prop) => {
            if (prop in (cloneTextRun?.styles || {})) {
                cloneTextRun.styles[prop] = null;
                cloneTextRun.styles[`raw-${prop}`] = true;
            }
        });
        return cloneTextRun;
    }
}

export default ClearFormat;
